/*
 * FILE: unified_ui/src/router.ts
 * PURPOSE: vue-router setup with all primary routes including the new /support route.
 *
 * Routes:
 *   /                  → redirect to /orders
 *   /chat              → AssistantChatView (the canonical chat surface)
 *   /orders            → OrdersView
 *   /orders/:id        → OrderDetailView
 *   /new-order         → NewOrderView (wizard)
 *   /support           → SupportView (new)
 *   /about             → AboutView
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { authHeaders } from './lib/sessionToken'

const ROUTE_LABELS: Record<string, string> = {
  chat: "Чат",
  orders: "КП",
  "order-detail": "Детали заказа",
  "new-order": "Получить новый КП",
  support: "Поддержка",
  about: "О приложении",
};

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/orders' },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('@/views/AssistantChatView.vue'),
  },
  // Old bookmarks (and the feature-flag rollout) land on the canonical chat.
  { path: '/assistant', redirect: (to) => ({ path: '/chat', query: to.query }) },
  { path: '/orders', name: 'orders', component: () => import('@/views/OrdersView.vue') },
  {
    path: '/orders/:id',
    name: 'order-detail',
    component: () => import('@/views/OrderDetailView.vue'),
    props: true,
  },
  { path: '/about', name: 'about', component: () => import('@/views/AboutView.vue') },
  { path: '/new-order', name: 'new-order', component: () => import('@/views/NewOrderView.vue') },
  { path: '/support', name: 'support', component: () => import('@/views/SupportView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/orders' },
]

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// Ensure / always redirects to /orders — belt and suspenders
router.beforeEach((to) => {
  if (to.path === '/') return '/orders'
  return true
})

// Track page views for ChatLayer observability
router.afterEach((to) => {
  const label = ROUTE_LABELS[String(to.name ?? '')] ?? to.name ?? to.path
  if (label) {
    void fetch("/api/chat-event", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      credentials: "include",
      body: JSON.stringify({
        event: "message.event",
        text: `👁 Открыта страница: ${label}`,
        meta: { event: "view-opened", route: String(to.name ?? to.path) },
      }),
    }).catch(() => {})
  }
})
